import { useEffect, useState } from "react";
import {
  smartFormatHms,
  validateTimeRange,
  validateTimeWithinDuration,
} from "../../../utils/hmsFormat";
import { DatePicker } from "../../DatePicker";
import { Icon } from "../../Icons";
import {
  type SuggestionData,
  SuggestionPopover,
} from "../../SuggestionPopover";
import { Tag } from "../../Tag";
import { TextField } from "../../TextField";
import { Tooltip } from "../../Tooltip";
import { Body } from "../../Typography/Body";
import { usePageNumberInput } from "../hooks/usePageNumberInput";

interface MetadataEditProps {
  /**
   * Date of the record (ISO string)
   */
  date: string | null;

  /**
   * Source tag (e.g., "AA")
   */
  sourceTag: string;

  /**
   * Start page number
   */
  startPageNumber: number;

  /**
   * End page number
   */
  endPageNumber: number;

  /**
   * Flag indicating this item is marked for creation to show the "newly added" tag
   */
  /**
   * `stack` (default) is the list's narrow left column — date above the page
   * range. `row` puts them on one line, which is what a full-width entry in
   * the comparison view needs.
   */
  layout?: "stack" | "row";
  markedForCreation?: boolean;

  /**
   * Flag indicating this item is marked for deletion to show the "deletion" tag
   */
  markedForDeletion?: boolean;

  /**
   * Callback when date changes
   */
  onDateChange?: (date: string | null) => void;

  /**
   * Callback when start page changes
   */
  onStartPageChange?: (value: number) => void;

  /**
   * Callback when end page changes
   */
  onEndPageChange?: (value: number) => void;

  /**
   * Row number to display (1-based)
   */
  rowNumber?: number;

  /**
   * DELETE_SEGMENT suggestion data (if any)
   */
  deleteSegmentSuggestion?: SuggestionData;

  /**
   * Whether there are text range suggestions for this item
   */
  hasTextRangeSuggestions?: boolean;

  /**
   * Whether any text range suggestion has been seen
   */
  textRangeSuggestionsSeen?: boolean;

  /**
   * Callback when suggestion is accepted or rejected
   */
  onSuggestionAction?: (
    suggestionId: string,
    action: "accept" | "reject",
  ) => void;

  /**
   * Callback to navigate/scroll to a related segment
   */
  onNavigateToSegment?: (segmentId: string) => void;

  /**
   * Key that changes when the item is reset/discarded, forcing local page inputs to re-sync
   */
  resetKey?: number;

  /**
   * Whether a (re-)summarization lambda is currently running for this
   * item. Drives the "Summarizing..." tag under the page inputs.
   */
  isRerunning?: boolean;

  /**
   * Whether this item recently finished a successful (re-)summarization.
   * Drives the transient "Summarized" tag under the page inputs.
   */
  isRecentlyRerun?: boolean;

  /**
   * Whether the most recent rerun for this item came back FAILED from the
   * lambda. Drives the red "Summarization failed" tag under the page inputs.
   */
  isRerunFailed?: boolean;

  /**
   * When true, the metadata column renders the sub-rosa variant: the page
   * range is replaced by two HH:MM:SS text inputs bound to
   * videoStartTime / videoEndTime, the source-tag label is hidden, and
   * each input shows a format-help tooltip.
   */
  isVideoEntry?: boolean;
  /**
   * Video timestamp ("HH:MM:SS"). Only consumed when `isVideoEntry`.
   */
  videoStartTime?: string | null;
  /**
   * Video timestamp ("HH:MM:SS"). Only consumed when `isVideoEntry`.
   */
  videoEndTime?: string | null;
  /**
   * Callback when the video start-time field changes. Empty string is a
   * valid value (means "no timestamp"). Only consumed when `isVideoEntry`.
   */
  onVideoStartTimeChange?: (value: string) => void;
  /**
   * Callback when the video end-time field changes.
   */
  onVideoEndTimeChange?: (value: string) => void;
  /**
   * Source video's duration in seconds, used to validate that the user
   * doesn't type a timestamp past the end of the clip. `null` /
   * `undefined` skips the check (e.g. probe still in flight). Only
   * consumed when `isVideoEntry`.
   */
  maxDurationSec?: number | null;
}

/**
 * Metadata section of the TimelineItem in edit mode
 * Displays editable date, source tag, and page numbers
 */
export const TimelineItemMetadataEdit: React.FC<MetadataEditProps> = ({
  date,
  sourceTag,
  layout = "stack",
  startPageNumber,
  endPageNumber,
  onDateChange,
  onStartPageChange,
  onEndPageChange,
  markedForCreation = false,
  markedForDeletion = false,
  rowNumber,
  deleteSegmentSuggestion,
  hasTextRangeSuggestions = false,
  textRangeSuggestionsSeen = false,
  onSuggestionAction,
  onNavigateToSegment,
  resetKey,
  isRerunning = false,
  isRecentlyRerun = false,
  isRerunFailed = false,
  isVideoEntry = false,
  videoStartTime,
  videoEndTime,
  onVideoStartTimeChange,
  onVideoEndTimeChange,
  maxDurationSec,
}) => {
  const startPage = usePageNumberInput(
    startPageNumber,
    onStartPageChange,
    resetKey,
  );
  const endPage = usePageNumberInput(endPageNumber, onEndPageChange, resetKey);

  // Sub-rosa variant — local state for the HH:MM:SS inputs so the user
  // can type freely. We push the raw value up on every keystroke and
  // re-format on blur (smartFormatHms tolerates any delimiter). Error
  // text is derived live and rendered under the inputs.
  const [startTimeLocal, setStartTimeLocal] = useState(videoStartTime ?? "");
  const [endTimeLocal, setEndTimeLocal] = useState(videoEndTime ?? "");
  // Resync from prop when the server-side value changes (e.g. discard
  // drafts) or when the parent bumps the resetKey.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    setStartTimeLocal(videoStartTime ?? "");
  }, [videoStartTime, resetKey]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    setEndTimeLocal(videoEndTime ?? "");
  }, [videoEndTime, resetKey]);
  const startTimeResult = smartFormatHms(startTimeLocal);
  const endTimeResult = smartFormatHms(endTimeLocal);
  // Per-field duration check: only meaningful once the field parses
  // cleanly, otherwise the format error already explains the problem.
  const startDurationError =
    startTimeResult.error === null
      ? validateTimeWithinDuration(startTimeLocal, maxDurationSec)
      : null;
  const endDurationError =
    endTimeResult.error === null
      ? validateTimeWithinDuration(endTimeLocal, maxDurationSec)
      : null;
  // Cross-field check fires only when both inputs individually parse —
  // otherwise the user is still mid-typing and shouldn't see a
  // "before/after" complaint on top of the per-field error.
  const rangeError =
    startTimeResult.error === null && endTimeResult.error === null
      ? validateTimeRange(startTimeLocal, endTimeLocal)
      : null;
  const startError = startTimeResult.error || startDurationError;
  const endError = endTimeResult.error || endDurationError;

  return (
    <div
      className={`indigo-timeline-item__metadata indigo-timeline-item__metadata--edit${
        layout === "row" ? " indigo-timeline-item__metadata--row" : ""
      }`}
    >
      <DatePicker
        value={date}
        onChange={onDateChange}
        variant="filled"
        className="indigo-timeline-item__date-picker"
      />

      <div className="indigo-timeline-item__source-edit">
        {isVideoEntry ? (
          // Two stacked rows ("Start ...", "End ..."). The metadata
          // column is narrow, so the legacy single-row "[start] - [end]"
          // layout overflowed into the right column. Stacking buys
          // each input the full column width and keeps the prefix
          // label compact.
          <div className="indigo-timeline-item__hms-inputs">
            <Body
              size="xs"
              className="indigo-timeline-item__source-tag-label !text-left"
            >
              {sourceTag}
            </Body>
            <Tooltip content="Video start time in HH:MM:SS" placement="top">
              <div className="indigo-timeline-item__hms-row">
                <Body size="xs" className="indigo-timeline-item__hms-label">
                  Start
                </Body>
                <span className="indigo-timeline-item__hms-field">
                  <TextField
                    value={startTimeLocal}
                    onChange={(value) => {
                      setStartTimeLocal(value);
                      onVideoStartTimeChange?.(value);
                    }}
                    onBlur={() => {
                      const { value } = smartFormatHms(startTimeLocal);
                      if (value !== startTimeLocal) {
                        setStartTimeLocal(value);
                        onVideoStartTimeChange?.(value);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      const { value } = smartFormatHms(startTimeLocal);
                      if (value !== startTimeLocal) {
                        setStartTimeLocal(value);
                        onVideoStartTimeChange?.(value);
                      }
                    }}
                    variant="filled"
                    areaSize="small"
                    noWrap
                    placeholder="HH:MM:SS"
                    isError={startError !== null}
                  />
                </span>
              </div>
            </Tooltip>
            <Tooltip content="Video end time in HH:MM:SS" placement="top">
              <div className="indigo-timeline-item__hms-row">
                <Body size="xs" className="indigo-timeline-item__hms-label">
                  End
                </Body>
                <span className="indigo-timeline-item__hms-field">
                  <TextField
                    value={endTimeLocal}
                    onChange={(value) => {
                      setEndTimeLocal(value);
                      onVideoEndTimeChange?.(value);
                    }}
                    onBlur={() => {
                      const { value } = smartFormatHms(endTimeLocal);
                      if (value !== endTimeLocal) {
                        setEndTimeLocal(value);
                        onVideoEndTimeChange?.(value);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      const { value } = smartFormatHms(endTimeLocal);
                      if (value !== endTimeLocal) {
                        setEndTimeLocal(value);
                        onVideoEndTimeChange?.(value);
                      }
                    }}
                    variant="filled"
                    areaSize="small"
                    noWrap
                    placeholder="HH:MM:SS"
                    isError={endError !== null || rangeError !== null}
                  />
                </span>
              </div>
            </Tooltip>
            {(startError || endError || rangeError) && (
              <Body
                size="xs"
                className="indigo-timeline-item__hms-error text-rose-500"
              >
                {startError || endError || rangeError}
              </Body>
            )}
          </div>
        ) : (
          <div className="indigo-timeline-item__page-inputs">
            <Body size="xs" className="indigo-timeline-item__source-tag-label">
              {sourceTag}
            </Body>
            <TextField
              value={startPage.localValue}
              onChange={startPage.handleChange}
              variant="filled"
              areaSize="small"
              noWrap
              className="indigo-timeline-item__page-input"
              placeholder=""
              isError={startPage.isError}
            />
            <Body size="xs" className="indigo-timeline-item__page-separator">
              -
            </Body>
            <TextField
              value={endPage.localValue}
              onChange={endPage.handleChange}
              variant="filled"
              areaSize="small"
              noWrap
              className="indigo-timeline-item__page-input"
              placeholder=""
              isError={endPage.isError}
            />
          </div>
        )}
      </div>
      <div className="indigo-timeline-item__tag-wrapper text-right">
        {isRerunning && (
          <Tag
            label="Summarizing..."
            variant="outlined"
            status="warning"
            size="medium"
          />
        )}
        {!isRerunning && isRerunFailed && (
          <Tag
            label="Summarization failed"
            variant="outlined"
            status="error"
            size="medium"
            className="whitespace-nowrap"
          />
        )}
        {!isRerunning && !isRerunFailed && isRecentlyRerun && (
          <Tag
            label="Summarized"
            variant="outlined"
            status="success"
            size="medium"
          />
        )}
        {markedForCreation && (
          <Tag label="Newly added" variant="outlined" size="medium" />
        )}
        {markedForDeletion && (
          <Tag
            label="Deleted"
            variant="outlined"
            status="error"
            size="medium"
          />
        )}
        {deleteSegmentSuggestion && !markedForDeletion && (
          <SuggestionPopover
            suggestion={deleteSegmentSuggestion}
            onAccept={() =>
              onSuggestionAction?.(deleteSegmentSuggestion.id, "accept")
            }
            onReject={() =>
              onSuggestionAction?.(deleteSegmentSuggestion.id, "reject")
            }
            onViewRelatedSegment={(() => {
              const relatedId = deleteSegmentSuggestion.relatedSegmentID;
              return relatedId && onNavigateToSegment
                ? () => onNavigateToSegment(relatedId)
                : undefined;
            })()}
            onNavigateToSegment={onNavigateToSegment}
          >
            <Tag
              label="Suggest delete"
              variant="outlined"
              status="warning"
              size="medium"
              className="cursor-pointer"
              icon={
                deleteSegmentSuggestion.seen ? (
                  <Icon name="check" sizeX={8} sizeY={8} />
                ) : undefined
              }
            />
          </SuggestionPopover>
        )}
        {hasTextRangeSuggestions && !markedForDeletion && (
          <Tag
            label="Suggest edit"
            variant="outlined"
            status="warning"
            size="medium"
            icon={
              textRangeSuggestionsSeen ? (
                <Icon name="check" sizeX={8} sizeY={8} />
              ) : undefined
            }
          />
        )}
      </div>
      <div className="flex-1"></div>
      {rowNumber !== undefined && (
        <Body size="xs" className="indigo-timeline-item__row-number text-right">
          #{rowNumber}
        </Body>
      )}
    </div>
  );
};
